/**
 * useWorkOrderRowDelete — the per-row Delete on the /workorders list
 * (2026-09-11). Mirrors the Projects page row delete (trash icon as the LAST
 * column via DataTable.onDeleteClick, admin-level gate, confirm → refetch)
 * but confirms through the hardened WoDeleteDialog and the shared
 * useWorkOrderDeleteFlow, so the row delete and the WO header delete run the
 * exact same cascade with the exact same ceremony.
 *
 * The dialog opens instantly from the row's own counts (tests, completed,
 * bookings, from-quote) and refines them once GET /delete-preview resolves.
 */
import { useCallback, useState, type ReactNode } from 'react';
import { Alert, Snackbar } from '@mui/material';
import { WoDeleteDialog } from '../project/WoDeleteDialog';
import { buildWoDeleteNotes, requiresReleaseFromPreview } from '../project/woDeleteNotes';
import {
  describeWorkOrderDeleteResult,
  useWorkOrderDeleteFlow,
} from '../../hooks/useWorkOrderDeleteFlow';

interface UseWorkOrderRowDeleteArgs {
  /** Refetch the list (and status counts) after a successful delete. */
  onDeleted: () => void;
}

interface RowCounts {
  testCount: number;
  completedCount: number;
  committedBookings: number;
  tentativeBookings: number;
  fromQuote: string | null;
}

const num = (v: unknown): number => (typeof v === 'number' ? v : Number(v) || 0);
const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v : null);

export function useWorkOrderRowDelete({ onDeleted }: UseWorkOrderRowDeleteArgs): {
  handleDeleteClick: (row: Record<string, unknown>) => void;
  dialog: ReactNode;
} {
  const [rowCounts, setRowCounts] = useState<RowCounts | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const flow = useWorkOrderDeleteFlow({
    onDeleted: (result, target) => {
      setToast(describeWorkOrderDeleteResult(target.workOrderId, result));
      onDeleted();
    },
  });

  const handleDeleteClick = useCallback((row: Record<string, unknown>) => {
    // Row fields come from WorkOrderTabRow (formatWorkOrderTableData keeps the raw keys).
    setRowCounts({
      testCount: num(row.SampleCount),
      completedCount: num(row.CompletedCount),
      committedBookings: num(row.ScheduleCommittedBookings),
      tentativeBookings: num(row.ScheduleTentativeBookings),
      fromQuote: str(row.FromQuoteNumber),
    });
    flow.openFor({
      workOrderKey: String(row.WorkOrderKey),
      workOrderId: str(row.WorkOrderId) ?? 'this work order',
      workOrderName: str(row.WorkOrderName),
      projectId: str(row.ProjectId),
    });
  }, [flow]);

  const p = flow.preview;
  // Before the preview lands, say what the row already knows.
  const fallbackNotes: string[] = [];
  if (!p && rowCounts) {
    const bookings = rowCounts.committedBookings + rowCounts.tentativeBookings;
    if (bookings > 0) {
      fallbackNotes.push(
        `Schedule attached: ${bookings} booking${bookings === 1 ? '' : 's'} — deleting releases the machine/people time.`,
      );
    }
    if (rowCounts.fromQuote) {
      fallbackNotes.push(`Converted from quote ${rowCounts.fromQuote} — the conversion link will be cleared.`);
    }
  }

  const dialog: ReactNode = (
    <>
      {flow.target && (
        <WoDeleteDialog
          open
          workOrderId={flow.target.workOrderId}
          workOrderName={flow.target.workOrderName}
          projectId={flow.target.projectId}
          testCount={p?.testCount ?? rowCounts?.testCount ?? 0}
          completedCount={p?.completedCount ?? rowCounts?.completedCount ?? 0}
          scheduleNote={null}
          notes={p ? buildWoDeleteNotes(p) : fallbackNotes}
          requireRelease={
            flow.forceRelease
            || requiresReleaseFromPreview(p)
            || (!p && (rowCounts?.committedBookings ?? 0) > 0)
          }
          busy={flow.busy}
          error={flow.error}
          onCancel={flow.close}
          onConfirm={(release) => { void flow.confirm(release); }}
        />
      )}
      <Snackbar
        open={toast !== null}
        autoHideDuration={8000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setToast(null)} variant="filled">
          {toast}
        </Alert>
      </Snackbar>
    </>
  );

  return { handleDeleteClick, dialog };
}
